import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalDataState } from '../components/PortalDataState';
import { PortalStatusPill } from '../components/PortalStatusPill';
import {
  filterSuppliersByLifecycle,
  partnerStatusPresentation,
  supplierLifecycleFilterOptions,
  type SupplierLifecycleFilter
} from '../services/collaboration/supplierCollaborationVocabulary';
import {
  collaborationPartnerRiskTierOptions,
  type CollaborationPartner,
  type CollaborationPartnerFootprintMode,
  type CollaborationPartnerFormState,
  type CollaborationPartnerRiskTier,
  type SupplierImportBatchSnapshot
} from './collaborationControlPlaneShared';

const riskTierLabel = (tier: CollaborationPartnerRiskTier): string =>
  collaborationPartnerRiskTierOptions.find((option) => option.value === tier)?.label ?? tier;

interface CollaborationPartnersCardProps {
  canManage: boolean;
  selectedTenantId: string;
  partnerForm: CollaborationPartnerFormState;
  partners: CollaborationPartner[];
  savingPartnerId: string;
  supplierImportFile: File | null;
  supplierImportBatch: SupplierImportBatchSnapshot | null;
  previewingSupplierImport: boolean;
  committingSupplierImport: boolean;
  relativeTime: (value: string) => string;
  onPartnerFormChange: (value: CollaborationPartnerFormState) => void;
  onCreatePartner: () => void;
  onSupplierImportFileChange: (file: File | null) => void;
  onPreviewSupplierImport: () => void;
  onCommitSupplierImport: () => void;
}

const CollaborationPartnersCard = ({
  canManage,
  selectedTenantId,
  partnerForm,
  partners,
  savingPartnerId,
  supplierImportFile,
  supplierImportBatch,
  previewingSupplierImport,
  committingSupplierImport,
  relativeTime,
  onPartnerFormChange,
  onCreatePartner,
  onSupplierImportFileChange,
  onPreviewSupplierImport,
  onCommitSupplierImport
}: CollaborationPartnersCardProps): JSX.Element => {
  const [lifecycleFilter, setLifecycleFilter] = useState<SupplierLifecycleFilter>('all');
  const [riskTierFilter, setRiskTierFilter] = useState<CollaborationPartnerRiskTier | 'all'>('all');
  const visiblePartners = useMemo(
    () => filterSuppliersByLifecycle(partners, lifecycleFilter).filter(
      (partner) => riskTierFilter === 'all' || (partner.riskTier ?? null) === riskTierFilter
    ),
    [lifecycleFilter, partners, riskTierFilter]
  );

  return (
    <div className="collaboration-supplier-layout">
    <section className="card">
      <div className="collaboration-section-heading">
        <div>
          <h3>Suppliers</h3>
          <p className="muted">Open a Supplier Profile to continue setup, manage contacts and routing, or review recent work.</p>
        </div>
        <PortalStatusPill
          label={`${partners.length} ${partners.length === 1 ? 'supplier' : 'suppliers'}`}
          tone={partners.length > 0 ? 'success' : 'neutral'}
        />
      </div>

      {partners.length === 0 ? (
        <PortalDataState
          kind="first-empty"
          heading="No suppliers yet"
          message="Add the organisation here. Its Supplier Profile will then guide you through contacts, Microsoft registration and activation."
          compact
        />
      ) : (
        <>
          <label className="collaboration-list-filter">
            <span className="muted">Show</span>
            <select
              className="input"
              value={lifecycleFilter}
              onChange={(event) => setLifecycleFilter(event.target.value as SupplierLifecycleFilter)}
            >
              {supplierLifecycleFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="collaboration-list-filter">
            <span className="muted">Risk tier</span>
            <select
              className="input"
              value={riskTierFilter}
              onChange={(event) => setRiskTierFilter(event.target.value as CollaborationPartnerRiskTier | 'all')}
            >
              <option value="all">Any</option>
              {collaborationPartnerRiskTierOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {visiblePartners.length === 0 ? (
            <PortalDataState
              kind="context-empty"
              heading="No suppliers in this view"
              message="Choose another lifecycle filter to see the remaining suppliers."
              compact
            />
          ) : (
            <div className="collaboration-supplier-list" style={{ marginTop: '12px' }}>
              {visiblePartners.map((partner) => {
                const status = partnerStatusPresentation(partner.status);
                return (
                  <Link
                    key={partner.partnerId}
                    className="collaboration-supplier-row"
                    to={`/collaboration/partners/${encodeURIComponent(partner.partnerId)}?tenantId=${encodeURIComponent(selectedTenantId)}`}
                  >
                    <span>
                      <strong>{partner.displayName}</strong>
                      <span className="muted collaboration-attention-detail">
                        {partner.externalSupplierReference ? `Reference ${partner.externalSupplierReference} · ` : ''}
                        {partner.supplierCategory ? `${partner.supplierCategory} · ` : ''}
                        {partner.riskTier ? `${riskTierLabel(partner.riskTier)} risk · ` : ''}
                        Updated {relativeTime(partner.updatedAt)}
                      </span>
                    </span>
                    <PortalStatusPill label={status.label} tone={status.tone} />
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>

    <div className="collaboration-profile-stack" style={{ marginTop: 0 }}>
      <section className="card">
        <h3>Add supplier</h3>
        <p className="muted">Create the organisation first. The new Supplier Profile will show the next valid setup step.</p>
        <div className="collaboration-form-grid">
          <label>
            <span className="muted">Supplier name</span>
            <input
              className="input"
              value={partnerForm.displayName}
              onChange={(event) => onPartnerFormChange({ ...partnerForm, displayName: event.target.value })}
              disabled={!canManage}
              required
            />
          </label>
          <label>
            <span className="muted">Primary domain (optional)</span>
            <input
              className="input"
              value={partnerForm.primaryDomain}
              onChange={(event) => onPartnerFormChange({ ...partnerForm, primaryDomain: event.target.value })}
              disabled={!canManage}
            />
          </label>
          <label>
            <span className="muted">Your supplier reference (optional)</span>
            <input
              className="input"
              value={partnerForm.externalSupplierReference}
              onChange={(event) => onPartnerFormChange({ ...partnerForm, externalSupplierReference: event.target.value })}
              disabled={!canManage}
            />
          </label>
          <label>
            <span className="muted">Category (optional)</span>
            <input
              className="input"
              value={partnerForm.supplierCategory}
              onChange={(event) => onPartnerFormChange({ ...partnerForm, supplierCategory: event.target.value })}
              placeholder="e.g. Raw materials, Logistics, Contract manufacturing"
              maxLength={100}
              disabled={!canManage}
            />
          </label>
          <label>
            <span className="muted">Risk tier (optional)</span>
            <select
              className="input"
              value={partnerForm.riskTier}
              onChange={(event) => onPartnerFormChange({ ...partnerForm, riskTier: event.target.value as CollaborationPartnerRiskTier | '' })}
              disabled={!canManage}
            >
              <option value="">Not set</option>
              {collaborationPartnerRiskTierOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted">Collaboration footprint</span>
            <select
              className="input"
              value={partnerForm.footprintMode}
              onChange={(event) =>
                onPartnerFormChange({
                  ...partnerForm,
                  footprintMode: event.target.value as CollaborationPartnerFootprintMode
                })
              }
              disabled={!canManage}
            >
              <option value="portal_only">Supplier Quality Workspace</option>
              <option value="collaboration_instance">Supplier also uses an Edinshall Audit installation</option>
            </select>
          </label>
        </div>
        <div className="collaboration-card-actions">
          <button
            className="btn"
            type="button"
            onClick={onCreatePartner}
            disabled={!canManage || !partnerForm.displayName.trim() || savingPartnerId === '__new__'}
          >
            {savingPartnerId === '__new__' ? 'Adding…' : 'Add supplier'}
          </button>
        </div>
      </section>

      <details className="card collaboration-disclosure">
        <summary>Import suppliers from CSV or Excel</summary>
        <p className="muted">
          Upload a .csv or .xlsx file with one supplier per row; the first worksheet of a workbook is used.
          Imported organisations start in setup. Review each Supplier Profile before activation.
        </p>
        <div className="collaboration-card-actions">
          <input
            className="input"
            type="file"
            aria-label="Supplier CSV or Excel file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => onSupplierImportFileChange(event.target.files?.[0] ?? null)}
            disabled={!canManage || previewingSupplierImport || committingSupplierImport}
          />
          <button
            className="btn secondary"
            type="button"
            onClick={onPreviewSupplierImport}
            disabled={!canManage || !supplierImportFile || previewingSupplierImport || committingSupplierImport}
          >
            {previewingSupplierImport ? 'Previewing…' : 'Preview file'}
          </button>
          {supplierImportBatch?.status === 'previewed' && (
            <button
              className="btn"
              type="button"
              onClick={onCommitSupplierImport}
              disabled={
                !canManage ||
                committingSupplierImport ||
                supplierImportBatch.rows.every((row) => row.validationStatus !== 'valid')
              }
            >
              {committingSupplierImport ? 'Importing…' : 'Import valid rows'}
            </button>
          )}
        </div>
        {supplierImportBatch && (
          <div style={{ marginTop: '12px', overflowX: 'auto' }}>
            <PortalStatusPill
              label={`${supplierImportBatch.rows.length} rows · ${supplierImportBatch.status}`}
              tone={supplierImportBatch.status === 'failed' ? 'danger' : 'neutral'}
            />
            <table className="table" style={{ marginTop: '10px' }}>
              <thead>
                <tr><th>Row</th><th>Supplier</th><th>Contact</th><th>Result</th></tr>
              </thead>
              <tbody>
                {supplierImportBatch.rows.slice(0, 25).map((row) => (
                  <tr key={row.importRowId}>
                    <td>{row.rowNumber}</td>
                    <td>{row.normalized.supplierName}</td>
                    <td>{row.normalized.contactEmail || '—'}</td>
                    <td>{row.commitStatus || row.validationStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {supplierImportBatch.rows.length > 25 && <p className="muted">Showing the first 25 rows.</p>}
          </div>
        )}
      </details>
    </div>
    </div>
  );
};

export default CollaborationPartnersCard;
